import { useState, useMemo } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    CalendarDays,
    Clock,
    Users,
    ChevronLeft,
    ChevronRight,
    Check,
    Loader2,
    Search,
    Phone,
    Mail,
    User,
    MessageSquare,
    Copy,
    XCircle,
} from "lucide-react";
import logoUrl from "@/assets/waiterix-logo.png";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import type { ReservationWithTables } from "@/shared/schema";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

/** Format a Date to YYYY-MM-DD using local timezone (avoids UTC shift from toISOString) */
function toLocalDateStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function formatTime(time: string) {
    const [h, m] = time.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function formatDate(dateStr: string) {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

type Step = "date" | "time" | "info" | "confirmation";

export default function ReservationBooking() {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const [step, setStep] = useState<Step>("date");
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<any | null>(null);
    const [partySize, setPartySize] = useState(2);
    const [customerName, setCustomerName] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [specialRequests, setSpecialRequests] = useState("");
    const [confirmedReservation, setConfirmedReservation] = useState<ReservationWithTables | null>(null);

    // Lookup state
    const [showLookup, setShowLookup] = useState(false);
    const [lookupCode, setLookupCode] = useState("");
    const [lookupResult, setLookupResult] = useState<any>(null);
    const [lookupError, setLookupError] = useState("");
    const [cancelPhone, setCancelPhone] = useState("");
    const [showCancelForm, setShowCancelForm] = useState(false);

    // Calendar state
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

    // Fetch reservation info
    const { data: info, isLoading: isInfoLoading, error: infoError } = useQuery<any>({
        queryKey: [`/api/public/restaurant/${restaurantId}/reservation-info`],
        enabled: !!restaurantId,
    });

    // Fetch available slots when date is selected
    const { data: slotsData, isLoading: isSlotsLoading } = useQuery<any>({
        queryKey: [`/api/public/restaurant/${restaurantId}/available-slots?date=${selectedDate}`],
        enabled: !!selectedDate && !!restaurantId,
    });

    // Create reservation mutation
    const createReservation = useMutation({
        mutationFn: async (data: any) => {
            const res = await apiRequest("POST", `/api/public/restaurant/${restaurantId}/reservations`, data);
            return res.json();
        },
        onSuccess: (data) => {
            setConfirmedReservation(data);
            setStep("confirmation");
        },
    });

    // Lookup mutation
    const lookupMutation = useMutation({
        mutationFn: async (code: string) => {
            const res = await apiRequest("GET", `/api/public/reservations/lookup?code=${encodeURIComponent(code)}`);
            return res.json();
        },
        onSuccess: (data) => {
            setLookupResult(data);
            setLookupError("");
        },
        onError: (error: Error) => {
            setLookupError(error.message);
            setLookupResult(null);
        },
    });

    // Cancel mutation
    const cancelMutation = useMutation({
        mutationFn: async ({ confirmationCode, customerPhone }: { confirmationCode: string; customerPhone: string }) => {
            const res = await apiRequest("POST", "/api/public/reservations/cancel", { confirmationCode, customerPhone });
            return res.json();
        },
        onSuccess: () => {
            setLookupResult({ ...lookupResult, status: "cancelled" });
            setShowCancelForm(false);
        },
    });

    // Generate calendar days
    const calendarDays = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxDate = new Date(today);
        maxDate.setDate(maxDate.getDate() + (info?.advanceBookingDays || 30));

        const firstDay = new Date(calendarYear, calendarMonth, 1);
        const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
        const startPad = firstDay.getDay();

        const days: { date: Date; dateStr: string; inMonth: boolean; enabled: boolean }[] = [];

        // Padding for previous month
        for (let i = 0; i < startPad; i++) {
            const d = new Date(calendarYear, calendarMonth, -startPad + i + 1);
            days.push({ date: d, dateStr: toLocalDateStr(d), inMonth: false, enabled: false });
        }

        // Days of current month
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const d = new Date(calendarYear, calendarMonth, i);
            const dateStr = toLocalDateStr(d);
            const dayOfWeek = d.getDay();

            // Check if in valid range
            const inRange = d >= today && d <= maxDate;

            // Check operating hours
            const opHours = info?.operatingHours?.find((h: any) => h.day === dayOfWeek);
            const isOpen = opHours && !opHours.closed;

            days.push({ date: d, dateStr, inMonth: true, enabled: inRange && !!isOpen });
        }

        return days;
    }, [calendarMonth, calendarYear, info]);

    const handleSubmit = () => {
        if (!selectedDate || !selectedSlot) return;
        createReservation.mutate({
            customerName,
            customerEmail: customerEmail || undefined,
            customerPhone: customerPhone || undefined,
            partySize,
            reservationDate: selectedDate,
            reservationTime: selectedSlot.time,
            specialRequests: specialRequests || undefined,
        });
    };

    const copyCode = () => {
        if (confirmedReservation?.confirmationCode) {
            navigator.clipboard.writeText(confirmedReservation.confirmationCode);
        }
    };

    // Error / loading states
    if (isInfoLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (infoError || !info) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="p-8 max-w-md text-center">
                    <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                    <h2 className="text-xl font-bold mb-2">Reservations Unavailable</h2>
                    <p className="text-muted-foreground">
                        This restaurant hasn't enabled online reservations yet.
                    </p>
                </Card>
            </div>
        );
    }

    // Lookup UI
    if (showLookup) {
        return (
            <div className="min-h-screen bg-background">
                <header className="border-b backdrop-blur-sm sticky top-0 z-50 bg-background/80">
                    <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                        <Link href="/" className="flex items-center gap-3">
                            <img src={logoUrl} alt="Waiterix" className="h-8" />
                        </Link>
                        <Button variant="ghost" size="sm" onClick={() => setShowLookup(false)}>
                            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Booking
                        </Button>
                    </div>
                </header>

                <main className="max-w-lg mx-auto px-4 py-12">
                    <h1 className="text-2xl font-bold mb-6">Look Up Your Reservation</h1>

                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <Input
                                placeholder="Enter confirmation code"
                                value={lookupCode}
                                onChange={(e) => setLookupCode(e.target.value.toUpperCase())}
                                className="uppercase tracking-wider font-mono"
                            />
                            <Button
                                onClick={() => lookupMutation.mutate(lookupCode)}
                                disabled={!lookupCode || lookupMutation.isPending}
                            >
                                {lookupMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                            </Button>
                        </div>

                        {lookupError && (
                            <p className="text-sm text-destructive">{lookupError}</p>
                        )}

                        {lookupResult && (
                            <Card className="p-6 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-semibold text-lg">{lookupResult.customerName}</h3>
                                    <Badge variant={
                                        lookupResult.status === "confirmed" ? "default" :
                                            lookupResult.status === "cancelled" ? "destructive" :
                                                lookupResult.status === "completed" ? "secondary" : "outline"
                                    }>
                                        {lookupResult.status}
                                    </Badge>
                                </div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-muted-foreground">Date</p>
                                        <p className="font-medium">{formatDate(lookupResult.reservationDate)}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Time</p>
                                        <p className="font-medium">{formatTime(lookupResult.reservationTime)} – {formatTime(lookupResult.endTime)}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Party Size</p>
                                        <p className="font-medium">{lookupResult.partySize} {lookupResult.partySize === 1 ? "guest" : "guests"}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Tables</p>
                                        <p className="font-medium">{lookupResult.assignedTables?.map((t: any) => `#${t.tableNumber}`).join(", ") || "TBD"}</p>
                                    </div>
                                </div>
                                {lookupResult.restaurantName && (
                                    <p className="text-sm text-muted-foreground">at {lookupResult.restaurantName}</p>
                                )}

                                {lookupResult.status !== "cancelled" && lookupResult.status !== "completed" && (
                                    <>
                                        {!showCancelForm ? (
                                            <Button variant="destructive" size="sm" onClick={() => setShowCancelForm(true)}>
                                                Cancel Reservation
                                            </Button>
                                        ) : (
                                            <div className="space-y-3 border-t pt-3">
                                                <p className="text-sm text-muted-foreground">Enter your phone number to confirm cancellation:</p>
                                                <Input
                                                    placeholder="Phone number"
                                                    value={cancelPhone}
                                                    onChange={(e) => setCancelPhone(e.target.value)}
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={() => cancelMutation.mutate({ confirmationCode: lookupResult.confirmationCode, customerPhone: cancelPhone })}
                                                        disabled={!cancelPhone || cancelMutation.isPending}
                                                    >
                                                        {cancelMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Cancellation"}
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => setShowCancelForm(false)}>
                                                        Back
                                                    </Button>
                                                </div>
                                                {cancelMutation.isError && (
                                                    <p className="text-sm text-destructive">{(cancelMutation.error as Error).message}</p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </Card>
                        )}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b backdrop-blur-sm sticky top-0 z-50 bg-background/80">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link href="/">
                            <img src={logoUrl} alt="Waiterix" className="h-8" />
                        </Link>
                        <div>
                            <h1 className="font-semibold text-sm leading-tight">{info.restaurantName}</h1>
                            <p className="text-xs text-muted-foreground">Online Reservation</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowLookup(true)}>
                        <Search className="h-4 w-4 mr-1" /> Look up
                    </Button>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8">
                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    {(["date", "time", "info", "confirmation"] as Step[]).map((s, i) => (
                        <div key={s} className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${step === s ? "bg-primary text-primary-foreground" :
                                (["date", "time", "info", "confirmation"].indexOf(step) > i) ? "bg-primary/20 text-primary" :
                                    "bg-muted text-muted-foreground"
                                }`}>
                                {(["date", "time", "info", "confirmation"].indexOf(step) > i) ? <Check className="h-4 w-4" /> : i + 1}
                            </div>
                            {i < 3 && <div className={`w-8 h-0.5 ${(["date", "time", "info", "confirmation"].indexOf(step) > i) ? "bg-primary/40" : "bg-muted"}`} />}
                        </div>
                    ))}
                </div>

                {/* Step 1: Date Selection */}
                {step === "date" && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
                                <CalendarDays className="h-6 w-6 text-primary" />
                                Choose a Date
                            </h2>
                            <p className="text-muted-foreground mt-1">Select your preferred date for the reservation</p>
                        </div>

                        <Card className="p-6">
                            {/* Calendar header */}
                            <div className="flex items-center justify-between mb-4">
                                <Button variant="ghost" size="icon" onClick={() => {
                                    if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(calendarYear - 1); }
                                    else setCalendarMonth(calendarMonth - 1);
                                }}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <h3 className="font-semibold">{MONTH_NAMES[calendarMonth]} {calendarYear}</h3>
                                <Button variant="ghost" size="icon" onClick={() => {
                                    if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(calendarYear + 1); }
                                    else setCalendarMonth(calendarMonth + 1);
                                }}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>

                            {/* Day names */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                                {DAY_NAMES.map((d) => (
                                    <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>
                                ))}
                            </div>

                            {/* Calendar grid */}
                            <div className="grid grid-cols-7 gap-1">
                                {calendarDays.map((day, idx) => (
                                    <button
                                        key={idx}
                                        disabled={!day.enabled}
                                        onClick={() => {
                                            setSelectedDate(day.dateStr);
                                            setSelectedSlot(null);
                                            setStep("time");
                                        }}
                                        className={`p-2 text-sm rounded-lg transition-all ${!day.inMonth ? "text-muted-foreground/30" :
                                            !day.enabled ? "text-muted-foreground/50 cursor-not-allowed" :
                                                selectedDate === day.dateStr ? "bg-primary text-primary-foreground font-semibold" :
                                                    "hover:bg-primary/10 font-medium cursor-pointer"
                                            }`}
                                    >
                                        {day.date.getDate()}
                                    </button>
                                ))}
                            </div>
                        </Card>
                    </div>
                )}

                {/* Step 2: Time Slot Selection */}
                {step === "time" && selectedDate && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
                                <Clock className="h-6 w-6 text-primary" />
                                Choose a Time
                            </h2>
                            <p className="text-muted-foreground mt-1">{formatDate(selectedDate)}</p>
                        </div>

                        <Card className="p-6">
                            {isSlotsLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            ) : slotsData?.message ? (
                                <div className="text-center py-12">
                                    <p className="text-muted-foreground">{slotsData.message}</p>
                                </div>
                            ) : !slotsData?.slots?.length ? (
                                <div className="text-center py-12">
                                    <p className="text-muted-foreground">No available time slots for this date</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {slotsData.slots.map((slot) => (
                                        <button
                                            key={slot.time}
                                            onClick={() => {
                                                setSelectedSlot(slot);
                                                setStep("info");
                                            }}
                                            className={`p-4 rounded-xl border text-center transition-all hover:border-primary hover:bg-primary/5 ${selectedSlot?.time === slot.time ? "border-primary bg-primary/10" : "border-border"
                                                }`}
                                        >
                                            <p className="font-semibold">{formatTime(slot.time)}</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {slot.availableSeats} seats available
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </Card>

                        <div className="flex justify-start">
                            <Button variant="ghost" onClick={() => setStep("date")}>
                                <ChevronLeft className="h-4 w-4 mr-1" /> Change Date
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 3: Customer Info */}
                {step === "info" && selectedDate && selectedSlot && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
                                <Users className="h-6 w-6 text-primary" />
                                Your Details
                            </h2>
                            <p className="text-muted-foreground mt-1">
                                {formatDate(selectedDate)} at {formatTime(selectedSlot.time)}
                            </p>
                        </div>

                        <Card className="p-6 space-y-5">
                            {/* Party size */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Users className="h-4 w-4" /> Number of Guests
                                </Label>
                                <div className="flex items-center gap-3">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setPartySize(Math.max(info.minPartySize, partySize - 1))}
                                        disabled={partySize <= info.minPartySize}
                                    >
                                        -
                                    </Button>
                                    <span className="text-2xl font-bold w-12 text-center">{partySize}</span>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setPartySize(Math.min(info.maxPartySize, partySize + 1))}
                                        disabled={partySize >= info.maxPartySize}
                                    >
                                        +
                                    </Button>
                                    <span className="text-sm text-muted-foreground ml-2">
                                        ({info.minPartySize}–{info.maxPartySize} guests)
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="name" className="flex items-center gap-2">
                                    <User className="h-4 w-4" /> Name *
                                </Label>
                                <Input
                                    id="name"
                                    placeholder="Your full name"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone" className="flex items-center gap-2">
                                    <Phone className="h-4 w-4" /> Phone (optional)
                                </Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    placeholder="+1 (555) 000-0000"
                                    value={customerPhone}
                                    onChange={(e) => setCustomerPhone(e.target.value)}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email" className="flex items-center gap-2">
                                    <Mail className="h-4 w-4" /> Email (optional)
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={customerEmail}
                                    onChange={(e) => setCustomerEmail(e.target.value)}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="requests" className="flex items-center gap-2">
                                    <MessageSquare className="h-4 w-4" /> Special Requests (optional)
                                </Label>
                                <textarea
                                    id="requests"
                                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    placeholder="Any dietary needs, celebrations, seating preferences..."
                                    value={specialRequests}
                                    onChange={(e) => setSpecialRequests(e.target.value)}
                                />
                            </div>

                            <Button
                                className="w-full"
                                size="lg"
                                onClick={handleSubmit}
                                disabled={!customerName.trim() || createReservation.isPending}
                            >
                                {createReservation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                    <Check className="h-4 w-4 mr-2" />
                                )}
                                Confirm Reservation
                            </Button>

                            {createReservation.isError && (
                                <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-5 text-center space-y-3">
                                    <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                                        <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-amber-800 dark:text-amber-200">This Time Slot Is Busy</p>
                                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                            {createReservation.error?.message || `There are no enough available seats for ${partySize} guests at this time.`}
                                        </p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50"
                                        onClick={() => {
                                            setStep("time");
                                            createReservation.reset();
                                        }}
                                    >
                                        <Clock className="h-4 w-4 mr-1" /> Try another time slot
                                    </Button>
                                </div>
                            )}
                        </Card>

                        <div className="flex justify-start">
                            <Button variant="ghost" onClick={() => setStep("time")}>
                                <ChevronLeft className="h-4 w-4 mr-1" /> Change Time
                            </Button>
                        </div>
                    </div>
                )}

                {/* Step 4: Confirmation */}
                {step === "confirmation" && confirmedReservation && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                                <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                            </div>
                            <h2 className="text-2xl font-bold">
                                {confirmedReservation.status === "confirmed" ? "Reservation Confirmed!" : "Reservation Submitted!"}
                            </h2>
                            <p className="text-muted-foreground mt-1">
                                {confirmedReservation.status === "confirmed"
                                    ? "Your table is reserved. See you there!"
                                    : "Your reservation is pending confirmation from the restaurant."}
                            </p>
                        </div>

                        <Card className="p-6 space-y-4">
                            {/* Confirmation code */}
                            <div className="text-center py-3 bg-muted rounded-lg">
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Confirmation Code</p>
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-3xl font-mono font-bold tracking-widest">{confirmedReservation.confirmationCode}</span>
                                    <Button variant="ghost" size="icon" onClick={copyCode} title="Copy code">
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-muted-foreground">Date</p>
                                    <p className="font-medium">{formatDate(confirmedReservation.reservationDate)}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Time</p>
                                    <p className="font-medium">{formatTime(confirmedReservation.reservationTime)} – {formatTime(confirmedReservation.endTime)}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Party Size</p>
                                    <p className="font-medium">{confirmedReservation.partySize} {confirmedReservation.partySize === 1 ? "guest" : "guests"}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Assigned Tables</p>
                                    <p className="font-medium">
                                        {confirmedReservation.assignedTables?.map((t) => `Table ${t.tableNumber}`).join(", ")}
                                    </p>
                                </div>
                            </div>

                            <div className="border-t pt-3 text-sm">
                                <p className="text-muted-foreground">Name: <span className="text-foreground">{confirmedReservation.customerName}</span></p>
                                <p className="text-muted-foreground">Phone: <span className="text-foreground">{confirmedReservation.customerPhone}</span></p>
                                {confirmedReservation.specialRequests && (
                                    <p className="text-muted-foreground">Notes: <span className="text-foreground">{confirmedReservation.specialRequests}</span></p>
                                )}
                            </div>
                        </Card>

                        <div className="flex gap-3 justify-center">
                            <Button variant="outline" onClick={() => {
                                setStep("date");
                                setSelectedDate(null);
                                setSelectedSlot(null);
                                setCustomerName("");
                                setCustomerPhone("");
                                setCustomerEmail("");
                                setSpecialRequests("");
                                setPartySize(2);
                                setConfirmedReservation(null);
                            }}>
                                Make Another Reservation
                            </Button>
                            <Button variant="ghost" onClick={() => setShowLookup(true)}>
                                <Search className="h-4 w-4 mr-1" /> Look Up Reservation
                            </Button>
                        </div>
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="border-t py-6 mt-12 bg-muted/30">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <p className="text-xs text-muted-foreground">
                        Powered by <Link href="/" className="text-primary hover:underline">Waiterix</Link>
                    </p>
                </div>
            </footer>
        </div>
    );
}
